import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { localizeMoyasarCheckoutUrl } from '@hero/core';
import type { Billing } from '@hero/schema';

/**
 * Open the hosted-checkout URL stamped on a Billing — that's all "Pay
 * now" does in the new flow.
 *
 * The frontend does NOT round-trip the backend on a Pay click. Each
 * Billing carries `Billing.checkout.{id,url}` (set when the Billing
 * was minted via `subscription/pay`), and that URL is the single
 * Moyasar invoice for that Billing's lifetime. Clicking Pay again on
 * the same Billing reopens the same Moyasar page. If the invoice has
 * gone terminal (paid / cancelled / expired), the customer needs to
 * start over via `/billing/plan` which creates a fresh Billing —
 * there is no in-place "recycle this Billing" path anymore.
 *
 * The cron + webhook handlers keep `Billing.status` in sync; the UI
 * just trusts the data it has and lets the user retry.
 *
 * The URL is run through `localizeMoyasarCheckoutUrl` with the user's
 * current `i18n.language` so Arabic users land on the Arabic page and
 * everyone else lands on the English page.
 */
export function useBillingPayManager() {
  const { i18n } = useTranslation();
  const payBilling = useCallback(
    (
      billing: Pick<Billing, 'checkout' | 'status'>,
      options?: {
        /**
         * - `'tab'` (default) — open Moyasar in a new tab so the user
         *   keeps the dashboard context.
         * - `'self'` — replace the current tab. Used on the first
         *   subscribe path where the user is signing up.
         */
        openIn?: 'self' | 'tab';
      },
    ) => {
      const rawUrl = billing.checkout?.url;
      if (!rawUrl) {
        // No checkout attached — the caller should have hidden the
        // Pay button. We log instead of throwing because UX should
        // degrade gracefully if a Billing race left checkout unset.
        // eslint-disable-next-line no-console
        console.warn(
          '[useBillingPayManager] Pay clicked on a Billing without a checkout URL — ignoring.',
        );
        return;
      }
      const url = localizeMoyasarCheckoutUrl(rawUrl, i18n.language);
      if (options?.openIn === 'self') {
        window.location.assign(url);
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    [i18n.language],
  );

  const payLatestUnpaid = useCallback(
    (billings: Billing[], options?: { openIn?: 'self' | 'tab' }) => {
      const unpaid = billings.find(
        b =>
          (b.status === 'pending' ||
            b.status === 'partially_paid' ||
            b.status === 'past_due') &&
          !!b.checkout?.url,
      );
      if (!unpaid) return;
      payBilling(unpaid, options);
    },
    [payBilling],
  );

  return {
    payBilling,
    payLatestUnpaid,
    /**
     * Always `false` — there is no async call to wait on. Kept for
     * call-site compatibility with the old shape; consumers can drop
     * their `isPaying` plumbing.
     */
    isPaying: false,
  };
}
